import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Ui/Button';
import { reportClientError } from '@/helpers/clientError';

interface FallbackProps {
    onReset: () => void;
}

function DefaultFallback({ onReset }: FallbackProps) {
    const { t } = useTranslation('pages');

    return (
        <div className='min-h-screen flex items-center justify-center p-6 bg-background'>
            <div className='max-w-md w-full text-center space-y-4'>
                <h1 className='text-xl font-semibold text-foreground'>
                    { t('Something went wrong') }
                </h1>
                <p className='text-muted-foreground'>
                    { t('An unexpected error occurred. Try again or reload the page.') }
                </p>
                <div className='flex gap-2 justify-center pt-2'>
                    <Button onClick={ onReset } variant='outline'>
                        { t('Try again') }
                    </Button>
                    <Button onClick={ () => {
                        window.location.reload();
                    } }>
                        { t('Reload page') }
                    </Button>
                </div>
            </div>
        </div>
    );
}

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
    state: State = {
        hasError: false,
    };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // eslint-disable-next-line no-console
        console.error('Render error caught by AppErrorBoundary:', error, errorInfo);

        reportClientError({
            component_stack: errorInfo.componentStack,
            message: error.message,
            source: 'render',
            stack: error.stack,
        });
    }

    handleReset = (): void => {
        this.setState({ hasError: false });
    };

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return <DefaultFallback onReset={ this.handleReset } />;
        }
        return this.props.children;
    }
}
